import { assert, hasOwnProperty, panic } from "@glideapps/ts-necessities";
import { areEqual, hashCodeOf, setIntersect, setSubtract, setUnion } from "collection-utils";
import createEmojiRegExp from "emoji-regex/text";
import entries from "lodash/entries";
import numeral from "numeral";
import { logError } from "./debug-print";

export { assert, assertNever, defined, dontAwait, mapFilterUndefined, panic, sleep } from "@glideapps/ts-necessities";

export type JSONData = unknown;
export type JSONObject = Record<string, JSONData>;

export const digitsAndLetters = Array.from("0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz")
    .sort()
    .join("");

/* eslint-disable */
export const emailDomainRegexp =
    /^@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])$/;
const emailAddressRegexp =
    /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
/* eslint-enable */

export function isValidEmailAddress(s: string): boolean {
    return emailAddressRegexp.test(s);
}

export function isUrl(maybeURL: string): boolean {
    try {
        new URL(maybeURL);
        return true;
    } catch {
        return false;
    }
}

export function getShortNameProblem(s: string): string | undefined {
    if (s.length < 5) {
        return "The name must be at least 5 characters long";
    }
    if (s.length > 32) {
        return "The name can't be more than 32 characters long";
    }
    if (!/^([a-z]|[0-9]|-)*$/.test(s)) {
        return "The name may only contain letters, digits, and the dash";
    }
    return undefined;
}

export function isValidShortName(s: string): boolean {
    return getShortNameProblem(s) === undefined;
}

function areTripleEqual<T>(a: T, b: T): boolean {
    return a === b;
}

export function shallowEqualArrays<T>(
    arrA: readonly T[] | undefined,
    arrB: readonly T[] | undefined,
    compareElements: (a: T, b: T) => boolean = areTripleEqual
) {
    if (arrA === arrB) {
        return true;
    }

    if (arrA === undefined || arrB === undefined) {
        return false;
    }

    const len = arrA.length;

    if (arrB.length !== len) {
        return false;
    }

    for (let i = 0; i < len; i++) {
        if (!compareElements(arrA[i], arrB[i])) {
            return false;
        }
    }

    return true;
}

// FIXME: This doesn't belong here
export const MaxPinLifeMins = 15;

// This exists purely to prevent buggy autocomplete from putting postal codes in tab names.
//
// It would be real neat if Chrome supported actual web standards.
// You're supposed to be able to say `autocomplete="off"` as an attribute to disable it,
// but Chrome adamantly refuses to support this (see https://bugs.chromium.org/p/chromium/issues/detail?id=468153#c164)
// What they recommend we do instead is "set a semantic tag". So here's the semantic meaning of _this_ attribute:
// stop autocompleting, you jerks.
//
// This works in Chrome and Firefox, but not Safari. Right now, Safari autocomplete
// hasn't done anything catastrophic so we may just have to live with this.
export const disableBrowserAutocompleteToken = "browsers-should-never-autocomplete-this";

export function checkBoolean(x: unknown): boolean {
    if (typeof x === "boolean") return x;
    return panic(`Value should be a boolean: ${x}`);
}

export function checkString(x: unknown): string {
    if (typeof x === "string") return x;
    return panic(`Value should be a string: ${x}`);
}

export function checkNumber(x: unknown): number {
    if (typeof x === "number") return x;
    return panic(`Value should be a number: ${x}`);
}

export function isArray<T, U>(x: readonly T[] | U): x is readonly T[];
export function isArray<T, U>(x: T[] | U): x is T[];
export function isArray<T, U>(x: readonly T[] | U): x is readonly T[] {
    return Array.isArray(x);
}

export function checkArray<T, U>(x: T[] | U, checkItem?: (item: unknown) => T): T[] {
    if (!Array.isArray(x)) {
        return panic(`Value should be an array: ${x}`);
    }
    if (checkItem !== undefined) {
        x.forEach(checkItem);
    }
    return x;
}

export function swapArray<T>(array: T[], srcOffset: number, dstOffset: number) {
    if (srcOffset === dstOffset) return;

    const tmp = array[dstOffset];
    array[dstOffset] = array[srcOffset];
    array[srcOffset] = tmp;
}

export function nonNull<T>(v: T | null, msg?: string): T {
    if (v === null) return panic(msg ?? "Value was null but should be non-null");
    return v;
}

export function isUndefinedish<T>(v: T | null | undefined): v is null | undefined {
    return v === null || v === undefined;
}

export function isDefined<T>(v: T | null | undefined): v is T {
    return v !== null && v !== undefined;
}

export function isEmptyOrUndefined(v: string | undefined): v is undefined {
    return v === undefined || v.length === 0;
}

export function isEmptyOrUndefinedish(v: string | undefined | null): v is undefined | null | "" {
    return v === undefined || v === null || v.length === 0;
}

export function nullToUndefined<T>(v: T | null): T | undefined {
    if (v === null) return undefined;
    return v;
}

/**
 * @deprecated Instead of `definedMapWithDefault(v,d,f)` use `defaultMap(v,f) ?? d`.
 */
export function definedMapWithDefault<T, U>(v: T | undefined, defaultValue: U, f: (v: T) => U): U {
    if (v === undefined) {
        return defaultValue;
    }
    return f(v);
}

export function truthify(x: unknown): boolean {
    return !!(x as boolean);
}

// This makes it type-safe
export function fillArray<T>(length: number, value: T): T[] {
    return new Array(length).fill(value);
}

export function undefinedIfEmptyString(s: unknown): string | undefined {
    if (s === undefined || s === "") return undefined;
    return checkString(s);
}

export function undefinedIfEmptyOrWhitespaceString(s: unknown): string | undefined {
    if (typeof s !== "string") return undefined;
    if (s.trim() === "") return undefined;
    return s;
}

export function removeArrayItem<T>(arr: ReadonlyArray<T>, index: number): T[] {
    return [...arr.slice(0, index), ...arr.slice(index + 1)];
}

export function replaceArrayItem<T>(arr: ReadonlyArray<T>, index: number, newItem: T): T[] {
    return [...arr.slice(0, index), newItem, ...arr.slice(index + 1)];
}

export function insertArrayItems<T>(arr: readonly T[], index: number, ...items: T[]): T[] {
    const newArr = [...arr];
    newArr.splice(index, 0, ...items);
    return newArr;
}

export function updateDefined<T>(old: T, updates: Partial<T>): T {
    const result: T = { ...old };
    for (const key of Object.keys(updates)) {
        const value = (updates as any)[key];
        if (value === undefined) continue;
        (result as any)[key] = value;
    }
    return result;
}

export function updateDeleteUndefined<T>(old: T, updates: Partial<T>): T {
    const result: T = { ...old };
    for (const key of Object.keys(updates)) {
        const value = (updates as any)[key];
        if (value !== undefined) {
            (result as any)[key] = value;
        } else {
            delete (result as any)[key];
        }
    }
    return result;
}

export function deleteUndefined<T>(o: T): T {
    const result: Partial<T> = {};
    for (const key of Object.keys(o)) {
        const value = (o as any)[key];
        if (value !== undefined) {
            (result as any)[key] = value;
        }
    }
    return result as T;
}

const extensionRegex = /^.+(\.[^./\\]+)$/;

export function pathExtension(path: string): string | undefined {
    const matches = path.match(extensionRegex);
    if (matches === null) return undefined;
    return matches[1];
}

export function isEmoji(str: string): boolean {
    return getEmoji(str) !== undefined;
}

export function getEmoji(str: string): string | undefined {
    const result = createEmojiRegExp().exec(str);
    if (result === null || !str.trimLeft().startsWith(result[0])) {
        return undefined;
    }

    // The Unicode Consortium considers [#*0-9] to be emoji in themselves.
    // They definitely can be modified into Emoji with diacritics but we don't
    // consider them alone to be emoji.
    if (result[0].match(/^[#*0-9]+$/) !== null) return undefined;

    return result[0];
}

// https://github.com/atlassian/react-beautiful-dnd/blob/master/stories/src/simple/simple.jsx
export function reorder<T>(list: ReadonlyArray<T>, startIndex: number, endIndex: number): Array<T> {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    return result;
}

export async function mapFilterUndefinedAsync<T, U>(
    iterable: Iterable<T>,
    f: (x: T, i: number) => Promise<U | undefined>
): Promise<U[]> {
    const result: U[] = [];
    let i = 0;
    for (const x of iterable) {
        const y = await f(x, i);
        i += 1;
        if (y === undefined) continue;
        result.push(y);
    }
    return result;
}

export function removeUndefinedProperties(obj: { [k: string]: unknown }): { [k: string]: unknown } {
    const result: any = {};
    for (const k of Object.keys(obj)) {
        const v = obj[k];
        if (v === undefined) continue;
        result[k] = v;
    }
    return result;
}

export function makeSingletonMap<K, V>(k: K, v: V): Map<K, V> {
    const m = new Map<K, V>();
    m.set(k, v);
    return m;
}

export function getCloudFunctionLocationForProject(
    project: string,
    locally: boolean = false,
    localPort: number = 5000,
    localHost: string = "localhost",
    region?: string
): string {
    if (region === undefined) {
        region = "us-central1";
    }

    const localLocation = `http://${localHost}:${localPort}/${project}/${region}`;
    return locally ? localLocation : `https://${region}-${project}.cloudfunctions.net`;
}

async function fetchGet(
    basePath: string,
    searchParams: { [key: string]: string },
    headers: { [key: string]: string } = {}
): Promise<Response | undefined> {
    const url = basePath + "?" + new URLSearchParams(searchParams).toString();
    try {
        return await fetch(url, { method: "GET", headers });
    } catch (e: unknown) {
        // FIXME: Appropriately handle exceptions here
        logError(`In fetchGet: ${e}`);
        return undefined;
    }
}

export async function fetchPageMetadata(
    target: string,
    locally: boolean = false
): Promise<{ title: string } | undefined> {
    const baseURL = locally
        ? getCloudFunctionLocationForProject("glide-page-metadata", true, 5050) + "/fetchPageMetadata"
        : "https://glide-page-metadata.firebaseapp.com/fetchPageMetadata";
    const result = await fetchGet(baseURL, { url: target });
    if (result === undefined || result.status !== 200) return undefined;
    return await result.json();
}

const anonymousUserRegex = /^anonymous-user-(([0-9a-zA-Z]+)-)?([0-9a-zA-Z]+)$/;

export function parseAnonymousUserID(uid: string): { appID: string; appUserID: string | undefined } | undefined {
    const match = uid.split("-{")[0].match(anonymousUserRegex);
    if (match === null) return undefined;
    const appID = match[3];
    let appUserID: string | undefined = match[2];
    if (appUserID !== undefined && appUserID.length === 0) {
        appUserID = undefined;
    }
    return { appID, appUserID };
}

const dateTimeSeparatorRegexp = /(.+\d\d)T(\d\d.+)/;

// This is a terrible hack.  We write date/times out to the sheet in
// ISO format, with the GMT time zone, which means they look like this:
//
//   2019-11-20T10:00:12.636Z
//
// We need to not interpret the time zone, however, because we treat
// everything as time-zone agnostic, but Chrono will interpret the time
// zone in that format.  To make it not do that we have to remove the
// "T" and the "Z".
export function sanitizeDateTime(str: string, removeSeparator: boolean): string {
    if (str.endsWith("Z")) {
        str = str.substring(0, str.length - 1);
    }
    if (removeSeparator) {
        // Safari can't do `new Date(str)` when the separator
        // is removed.
        const match = str.match(dateTimeSeparatorRegexp);
        if (match !== null) {
            str = match[1] + " " + match[2];
        }
    }
    return str;
}

export function makeDateFromString(s: string, removeUTCMarker: boolean = false): Date | undefined {
    s = s.trim();
    if (s === "") return undefined;

    if (removeUTCMarker) {
        // We need this whenever the caller expects the date/time to be
        // interpreted as local time, no matter what.  That's the case
        // in `asDate`, where the date might come with or without the
        // `Z` prefix, but we need to parse it in local time in either
        // case, so `asDate` can safely convert it to time zone agnostic.
        s = sanitizeDateTime(s, false);
    }
    let d = new Date(s);
    if (!isNaN(d as any)) {
        return d;
    }
    // This is how Google Sheets represents raw times
    d = new Date("12/30/1899 " + s);
    if (!isNaN(d as any)) {
        return d;
    }
    return undefined;
}

export function convertDateToTimeZoneAgnostic(d: Date): Date {
    return new Date(d.getTime() - d.getTimezoneOffset() * 60 * 1000);
}

export function convertDateFromTimeZoneAgnostic(d: Date): Date {
    // So, convertDateToTimeZoneAgnostic() properly coerces Dates to UTC.
    // But coercion back to Time Zone Aware across a DST boundary breaks
    // horribly.
    //
    // Consider this example, valid in any US timezone observing DST,
    // happening on the time boundary of a transition into DST (You will
    // have to explicitly set your clock to such a timezone to reproduce this):
    //
    // convertDateFromTimeZoneAgnostic(
    //     convertDateToTimeZoneAgnostic(new Date("2020-03-08 3:00"))
    // ).getHours() === 2 // We expected 3 here
    //
    // We want the UTC hours and UTC minutes of the UTC date to
    // match the non-UTC hours and non-UTC minutes of the converted non-UTC
    // date, but they cannot whenever the UTC offset conversion in
    // convertDateToTimeZoneAgnostic switches time zones by aligning to UTC.
    //
    // What we do know is that whenever this happens, the UTC hours and minutes
    // of the "UTC date" don't match up with the non-UTC hours and minutes of
    // the non-UTC date. To correct for this, we
    // 1. convert the wrong non-UTC date to a wrong UTC date
    // 2. compute the difference between the right UTC date and the wrong UTC date
    // 3. Add that difference to the wrong non-UTC date to get the right non-UTC date.
    //
    // This works because time zones are (currently) only granular in terms of hours
    // and minutes. And we don't have to worry about wrapping around by days either,
    // because worst-case timezone offsets are always less than 24 hours.
    const basis = new Date(d.getTime() + d.getTimezoneOffset() * 60 * 1000);
    if (basis.getHours() === d.getUTCHours() && basis.getMinutes() === d.getUTCMinutes()) {
        return basis;
    }
    const basisAsUTC = Date.UTC(
        basis.getFullYear(),
        basis.getMonth(),
        basis.getDate(),
        basis.getHours(),
        basis.getMinutes(),
        basis.getSeconds()
    );
    return new Date(basis.getTime() + (d.getTime() - basisAsUTC));
}

export function getNowTimeZoneAgnostic(): Date {
    return convertDateToTimeZoneAgnostic(new Date());
}

export function makeStartOfDay(d: Date): Date {
    // This will give the start of day in the local timezone
    return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

function getDateString(d: Date): string {
    const asString = d.toISOString();
    return asString.substring(0, asString.indexOf("T"));
}

export function getISODateString(d: Date): string {
    return getDateString(makeStartOfDay(d));
}

// This assumes `d` is time-zone agnostic, and returns a time-zone
// agnostic Date.
export function makeStartOfDayTimeZoneAgnostic(d: Date): Date {
    return convertDateToTimeZoneAgnostic(makeStartOfDay(d));
}

export function makeEndOfDayTimeZoneAgnostic(d: Date): Date {
    // The -1 ensures that the end of the day lands on the same day
    // as the start of the day; previously this would land on "tomorrow"
    // every day except for a leap second day.
    //
    // This relies on Date.getTime() being guaranteed by the ECMA specification
    // to always return an integral number of milliseconds since the Unix epoch.
    // It's going to be 1ms wrong whenever a leap second occurs. This is probably fine.
    return new Date(makeStartOfDayTimeZoneAgnostic(d).getTime() + 24 * 60 * 60 * 1000 - 1);
}

export function parseDateTimeZoneAgnostic(str: string): Date {
    const d = makeDateFromString(str);
    if (d === undefined) {
        return getNowTimeZoneAgnostic();
    }
    return convertDateToTimeZoneAgnostic(d);
}

export function parseNumber(str: string): number | undefined {
    try {
        // Treat commas as decimal points, or remove them if there's
        // already a decimal point.
        if (str.includes(".")) {
            str = str.replace(/,/g, "");
        } else {
            str = str.replace(/,/g, ".");
        }
        const n = numeral(str).value();
        if (typeof n !== "number") return undefined;
        if (isNaN(n)) return undefined;
        return n;
    } catch {
        return undefined;
    }
}

export function parseNumberDiligently(str: string): number | undefined {
    const x = parseNumber(str);
    if (x === undefined) return undefined;
    if (x.toString() !== str) return undefined;
    return x;
}

export function getFirstNameOfUsername(username: string): string {
    return username.split(" ")[0];
}

export function parseURL(s: string): URL | undefined {
    try {
        return new URL(s);
    } catch {
        return undefined;
    }
}

// We have to engage in generics because URL is already in the global
// scope in browsers, but needs to be imported as a package in Node.
export function liftURLAssumingHTTP<T>(possibleURL: string, urlConstructor: (p: string) => T): T | undefined {
    try {
        return urlConstructor(possibleURL);
    } catch {
        // Yuck, but... people do this.
        try {
            return urlConstructor(`http://${possibleURL}`);
        } catch {
            return undefined;
        }
    }
}

export function findType<T, U extends T>(iterable: Iterable<T>, f: (t: T) => t is U): U | undefined {
    for (const t of iterable) {
        if (f(t)) {
            return t;
        }
    }
    return undefined;
}

export function filterType<T, U extends T>(iterable: Iterable<T>, f: (t: T) => t is U): U[] {
    const result: U[] = [];
    for (const t of iterable) {
        if (f(t)) {
            result.push(t);
        }
    }
    return result;
}

export function objectKeyForValue<T extends string, U>(
    o: { [k in T]?: U },
    v: U,
    equal?: (a: U | undefined, b: U) => boolean
): T | undefined {
    for (const k of Object.keys(o) as T[]) {
        const ov = o[k];
        const eq = equal !== undefined ? equal(ov, v) : ov === v;
        if (eq) return k;
    }
    return undefined;
}

export function normalizeEmailAddress(s: string): string {
    return s.toLowerCase().trim();
}

export function mapNewOrUpdate<K, V>(map: Map<K, V>, k: K, newValue: () => V, updateValue: (v: V) => V): V {
    let v = map.get(k);
    if (v === undefined) {
        v = newValue();
    } else {
        v = updateValue(v);
    }
    map.set(k, v);
    return v;
}

export function isResponseOK(r: Response | undefined): r is Response & { ok: true } {
    return r !== undefined && r.ok;
}

export function ignore(..._args: any) {
    /// does nothing
}

export async function getResponseErrorMessage(r: Response | undefined): Promise<string | undefined> {
    assert(!isResponseOK(r));
    if (r === undefined) return undefined;
    const json = await r.json();
    if (hasOwnProperty(json, "message") && typeof json.message === "string") {
        return json.message;
    }
    return undefined;
}

export function escapeHtml(unsafe: string) {
    return unsafe
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");
}

// https://makandracards.com/makandra/15879-javascript-how-to-generate-a-regular-expression-from-a-string
export function escapeStringAsRegexp(string: string): string {
    return string.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&");
}

export type TextTemplateToken<T> =
    | { readonly isText: true; readonly text: string }
    | { readonly isText: false; readonly value: T };

export function tokenizeTemplateString(
    template: string,
    patterns: readonly string[]
): readonly TextTemplateToken<string>[] {
    patterns = patterns.filter(p => p !== "");
    if (patterns.length === 0) {
        return [{ isText: true, text: template }];
    }

    const regexp = new RegExp(patterns.map(escapeStringAsRegexp).join("|"), "g");
    const tokens: TextTemplateToken<string>[] = [];

    function pushLiteral(text: string) {
        if (text === "") return;
        tokens.push({ isText: true, text });
    }

    let lastIndex = 0;
    for (;;) {
        const match = regexp.exec(template);
        if (match === null) break;

        const { index } = match;
        const pattern = match[0];

        pushLiteral(template.substr(lastIndex, index - lastIndex));
        lastIndex = index + pattern.length;

        tokens.push({ isText: false, value: pattern });
    }
    pushLiteral(template.substr(lastIndex));

    return tokens;
}

export function isOlderThan(date: Date, ms: number): boolean {
    const timeout = new Date(date.getTime() + ms);
    const now = new Date();
    return now > timeout;
}

export function isWithinWindowFromNow(date: Date, windowMS: number): boolean {
    const now = new Date();
    const future = new Date(now.getTime() + windowMS);
    // If the date is in the past, it is not within a future window
    if (date < now) {
        return false;
    }
    return date < future;
}

export function makeGoogleSheetURL(fileID: string): string {
    return `https://docs.google.com/spreadsheets/d/${fileID}`;
}

export function getBrowserLanguage(): string | undefined {
    try {
        return checkString(navigator.language);
    } catch {
        return undefined;
    }
}

export async function maybeAsync<T>(fn: () => Promise<T>, defaultValue: T) {
    try {
        const result = await fn();
        return result;
    } catch {
        return defaultValue;
    }
}

export function maybe<T>(fn: () => T, defaultValue: T) {
    try {
        const result = fn();
        return result;
    } catch {
        return defaultValue;
    }
}

const charCodeA = "A".charCodeAt(0);

function lettersFromInteger(i: number): string {
    if (i < 26) {
        return String.fromCharCode(charCodeA + i);
    } else {
        const x = i % 26;
        const y = (i - x) / 26;
        return lettersFromInteger(y - 1) + lettersFromInteger(x);
    }
}

export function makeNameUnique(name: string, nameExists: (n: string) => boolean): string {
    if (!nameExists(name)) {
        return name;
    }

    let index = 0;
    for (;;) {
        const withIndex = name + " " + lettersFromInteger(index);
        if (!nameExists(withIndex)) {
            return withIndex;
        }
        index++;
    }
}

const urlSchemaRegex = /^[a-zA-Z][a-zA-Z0-9+.-]+:/;

// s: string | undefined to simplify some usages.
// See ListItem.ts as an example: href is an optional prop; if we just pass
// in the `undefined` we expect from a non-passed prop then the logic for
// checking whether to make the item clickable is still valid.
export function safeURL(s: string | null | undefined): string | undefined {
    if (s === null || s === undefined) return undefined;
    s = s.trim();
    if (s === "") return undefined;

    if (s.match(urlSchemaRegex) === null) {
        const emoji = getEmoji(s);
        if (emoji !== undefined) {
            s = `https://emojipedia.org/${emoji}`;
        } else {
            s = "https://" + s;
        }
    }

    const parsed = parseURL(s);
    if (parsed === undefined) return undefined;

    // FIXME: What other schemes do we not want?
    // eslint-disable-next-line
    if (parsed.protocol === "javascript:") return undefined;

    return s;
}

export function objectWithUndefinedProperties(keys: readonly string[]): JSONObject {
    const o: JSONObject = {};
    for (const c of keys) {
        o[c] = undefined;
    }
    return o;
}

export function keyValuePairs<K extends keyof any, V>(obj: Record<K, V>): [K, V][] {
    return entries(obj) as [K, V][];
}

export function makeRandomPassword(): string {
    // FIXME: // use the cute haiku thing
    return Math.round(Math.random() * 100000).toString();
}

export function getCurrentTimestampInMilliseconds(): number {
    try {
        if (window.performance !== undefined && window.performance.now !== undefined) {
            return window.performance.now();
        }
    } catch {
        // nothing to do
    }
    return Date.now();
}

// FIXME: Move to collection-utils.
export class EqualitySet<K> {
    private readonly _map = new Map<number, K>();

    public add(k: K): void {
        let h = hashCodeOf(k) | 0;
        for (;;) {
            const inMap = this._map.get(h);
            if (inMap === undefined) {
                this._map.set(h, k);
                return;
            }
            if (areEqual(k, inMap)) {
                return;
            }
            h = (h + 1) | 0;
        }
    }

    public has(k: K): boolean {
        let h = hashCodeOf(k) | 0;
        for (;;) {
            const inMap = this._map.get(h);
            if (inMap === undefined) {
                return false;
            }
            if (areEqual(k, inMap)) {
                return true;
            }
            h = (h + 1) | 0;
        }
    }

    values(): IterableIterator<K> {
        return this._map.values();
    }
}

// FIXME: Move to collection-utils
export function mapRenameKey<K, V>(map: Map<K, V>, oldKey: K, newKey: K): void {
    assert(!map.has(newKey));
    if (!map.has(oldKey)) return;
    const v = map.get(oldKey);
    // The case is necessary because TS doesn't know that `undefined` is ok
    map.set(newKey, v as V);
    map.delete(oldKey);
}

const durationConversionFactor = 24 * 60 * 60 * 1000;

// Glide durations are like Google Sheet durations: numbers
// with unit "day", i.e. 0.5 means 12 hour, 1 means 1 day, etc.
export function convertJSDurationToGlide(jsDuration: number): number {
    return jsDuration / durationConversionFactor;
}

export function convertGlideDurationToJS(glideDuration: number): number {
    return glideDuration * durationConversionFactor;
}

export const epochDaySerial = 25569;

// These are for converting from/to Google Sheet day serials,
// which are encoded as the duration since `12/30/1899 0:00:00`.
// Dates are time-zone agnostic.
export function convertDaySerialToDate(daySerial: number): Date | undefined {
    const value = new Date(Math.round((daySerial - epochDaySerial) * durationConversionFactor));
    if (isNaN(value as any)) return undefined;
    return value;
}

export function convertDateToDaySerial(date: Date): number {
    return date.getTime() / durationConversionFactor + epochDaySerial;
}

export function glideViralReferralLink(utm_content: string): string {
    const params = {
        utm_source: "glide",
        utm_campaign: "player",
        utm_content,
        dr: window.location.href,
    };
    const query = Object.entries(params)
        .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
        .join("&");
    return `https://www.glideapps.com?${query}`;
}

export function mapRecord<T, U>(r: Record<string, T>, f: (v: T, n: string) => U): Record<string, U> {
    const result: Record<string, U> = {};
    for (const [name, value] of entries(r)) {
        result[name] = f(value, name);
    }
    return result;
}

export function areSetsEqual<T>(left: Iterable<T>, right: Iterable<T>): boolean {
    let rightSet: Set<T>;
    if (right instanceof Set) {
        rightSet = right;
    } else {
        rightSet = new Set(right);
    }
    return setSubtract(setUnion(left, rightSet), setIntersect(left, rightSet)).size === 0;
}

export function exceptionToString(e: unknown): string {
    if (e === undefined) return "";
    try {
        return (e as any).toString();
    } catch (f: unknown) {
        try {
            return `Exception can't be stringified: ${exceptionToString(f)}`;
        } catch {
            return "Exception can't be stringified";
        }
    }
}

export function exceptionToError(e: unknown): Error {
    if (e instanceof Error) {
        return e;
    } else {
        return new Error(exceptionToString(e));
    }
}

// Proves that type U is a subtype of type T.
// Does nothing at runtime.
export function proveSubtype<_T, _U extends _T>() {
    return;
}

export function withStopwatch<T>(f: () => T): [number, T] {
    const start = Date.now();
    const result = f();
    const time = Date.now() - start;
    return [time, result];
}

const numberPrefixRegexp = /^-?[0-9,.]+/;

function getNumberPrefix(s: string): { n: number; rest: string } | undefined {
    const match = s.match(numberPrefixRegexp);
    if (match === null) return undefined;
    const numberString = match[0];
    const n = parseNumber(numberString);
    if (n === undefined) return undefined;
    return {
        n,
        rest: s.substring(numberString.length),
    };
}

export function asMaybeTrueOrFalse(s: string): boolean | undefined {
    s = s.toLowerCase();
    if (s === "true" || s === "1") return true;
    if (s === "false" || s === "0") return false;
    return undefined;
}

export function compareStrings(one: string, two: string): number {
    const p1 = getNumberPrefix(one);
    if (p1 !== undefined) {
        const p2 = getNumberPrefix(two);
        if (p2 !== undefined) {
            if (p1.n < p2.n) return -1;
            if (p1.n > p2.n) return 1;

            one = p1.rest;
            two = p2.rest;
        }
    }

    const b1 = asMaybeTrueOrFalse(one);
    if (b1 !== undefined) {
        const b2 = asMaybeTrueOrFalse(two);
        if (b2 !== undefined) {
            if (b1 === b2) return 0;
            if (b1 === false) return -1;
            return 1;
        }
    }

    return one.localeCompare(two);
}

export function filterUndefined<T>(arr: Iterable<T | undefined>): T[] {
    const result: T[] = [];
    for (const x of arr) {
        if (x !== undefined) {
            result.push(x);
        }
    }
    return result;
}

export function mapFilter<K, V>(m: ReadonlyMap<K, V>, pred: (v: V) => boolean): Map<K, V> {
    const result = new Map<K, V>();
    for (const [k, v] of m) {
        if (!pred(v)) continue;
        result.set(k, v);
    }
    return result;
}

export function areSetsOverlapping<T>(a: ReadonlySet<T>, b: Iterable<T> | ReadonlySet<T>): boolean {
    if (b instanceof Set && a.size < b.size) {
        for (const key of a) {
            if (b.has(key)) return true;
        }
    } else {
        for (const key of b) {
            if (a.has(key)) return true;
        }
    }
    return false;
}

// `afterItem === undefined` means move it to the start.
export function moveItemInArray<T>(items: readonly T[], item: T, afterItem: T | undefined): readonly T[] | undefined {
    const startIndex = items.indexOf(item);
    if (startIndex < 0) return undefined;

    if (afterItem === undefined) {
        return reorder(items, startIndex, 0);
    } else {
        const newItems = items.filter(t => t !== item);
        const afterIndex = newItems.indexOf(afterItem);
        if (afterIndex < 0) return undefined;

        newItems.splice(afterIndex + 1, 0, item);
        return newItems;
    }
}

export enum VisitResult {
    Stop = "glide_visit_result_stop",
    SkipChildren = "glide_visit_result_skip",
    Continue = "glide_visit_result_continue",
}

export function visit<T, TResult>(
    items: readonly T[],
    childrenForItem: (i: T) => undefined | readonly T[],
    onVisit: (i: T) => VisitResult | TResult
): TResult | undefined {
    const toVisit = [...items].reverse();
    while (toVisit.length > 0) {
        const item = toVisit.pop();
        if (item === undefined) return undefined;

        const result = onVisit(item);
        if (result === VisitResult.Stop) return undefined;
        if (result === VisitResult.SkipChildren) continue;
        if (result !== VisitResult.Continue) {
            return result;
        }
        const children = childrenForItem(item);
        if (children !== undefined && children.length > 0) {
            toVisit.push(...children);
        }
    }
    return undefined;
}

export function anyDefined(...args: any[]): boolean {
    return args.some(a => {
        if (typeof a === "string") {
            return a !== undefined && a !== "";
        }
        return a !== undefined;
    });
}

export function objectKeys<T extends string | symbol | number>(obj: Record<T, unknown>): T[] {
    return Object.keys(obj) as T[];
}

export function removeNullFromObject<T>(obj: Record<string, T | null>): Record<string, T> {
    const cleaned: Record<string, T> = {};
    for (const [k, v] of Object.entries(obj)) {
        if (v === null) continue;
        cleaned[k] = v;
    }
    return cleaned;
}
